/**
 * Unified Push Notification Service for Capacitor
 *
 * Bridges native push notifications (iOS/Android) with the existing web push
 * implementation. Automatically uses the appropriate implementation per platform.
 *
 * OWNERSHIP (Card 15). This module is the SINGLE owner of native push listener
 * registration and of `PushNotifications.register()`. Before Card 15 there were two
 * owners: `initializeCapacitor()` called `registerForPushNotifications()` with **no**
 * handlers at startup, and `PushNotificationPrompt` called it again **with** handlers —
 * so `register()` fired twice and the startup path installed no listeners at all, which
 * meant an already-granted user could never re-sync their token.
 *
 * The split now is:
 *   - this module owns listeners + `register()`, both idempotent per app session;
 *   - callers that want to observe push events call `addPushHandlers()`, which is a
 *     subscription on top of the one listener set — it never installs listeners of its own;
 *   - `PushNotificationPrompt` owns only the permission *UX*.
 *
 * Web is untouched: every native path is behind the `isNative` guard, and web push stays
 * with the service worker + VAPID flow in `PushNotificationPrompt`.
 */

import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { isNative, isAndroid, platform } from '@/utils/platform';
import { getSessionId } from '@/services/nativeSession';
import {
  clearRegistrationError,
  getNativeBuild,
  recordDeliverySeen,
  recordRegistrationError
} from './pushDiagnostics';
import axios from 'axios';

// Types for push notification handling
export interface PushSubscription {
  token: string;
  platform: 'ios' | 'android' | 'web';
}

export interface PushNotificationHandlers {
  onRegistration?: (token: string) => void;
  onRegistrationError?: (error: any) => void;
  onNotificationReceived?: (notification: PushNotificationSchema) => void;
  onNotificationAction?: (action: ActionPerformed) => void;
}

/**
 * Android notification channel. Android 8+ DROPS any notification posted to a channel
 * that does not exist, so this id is a three-way contract and all three must match:
 *   - here (the channel is actually created by `ensureNotificationChannel()` below)
 *   - android/app/src/main/res/values/strings.xml → default_notification_channel_id
 *     (the manifest fallback the FCM SDK uses for messages with no channel id)
 *   - api_backend_fastify_server/src/services/push/fcm.service.ts → ANDROID_CHANNEL_ID
 */
export const ANDROID_CHANNEL_ID = 'trade_loop_default';

/** Result of asking for notification permission. */
export type PushPermissionStatus = 'granted' | 'denied' | 'prompt';

// ---------------------------------------------------------------------------
// Module-owned state. All of it is per app session (the WebView is torn down on
// app kill, so nothing here needs to survive a cold start).
// ---------------------------------------------------------------------------

/** Listeners are installed exactly once, no matter how many callers ask. */
let listenersInstalled = false;
/** `PushNotifications.register()` is fired exactly once per session. */
let registerCalled = false;
/** Channel creation is attempted once per session. */
let channelEnsured = false;
/** Every subscriber added via `addPushHandlers`. The one listener set fans out to these. */
const handlerSubscriptions = new Set<PushNotificationHandlers>();

/**
 * A tap-through destination that arrived before anything could route it.
 *
 * A killed-state tap launches the app AND fires `pushNotificationActionPerformed` well
 * before React mounts and the router exists, so the destination has to be parked until
 * someone can navigate. Without this the tap lands the user on the default route with no
 * indication anything was lost.
 */
let pendingNavigation: string | null = null;
let navigationHandler: ((path: string) => void) | null = null;

/**
 * Register the app's router as the navigation target for notification taps.
 * Flushes any destination that arrived before the router existed.
 * Returns an unsubscribe function.
 */
export function setPushNavigationHandler(handler: (path: string) => void): () => void {
  navigationHandler = handler;

  if (pendingNavigation) {
    const path = pendingNavigation;
    pendingNavigation = null;
    handler(path);
  }

  return () => {
    if (navigationHandler === handler) navigationHandler = null;
  };
}

/**
 * Subscribe to push events. Does NOT install listeners or trigger registration — those
 * belong to this module (see the ownership note at the top of the file). Safe to call
 * before or after registration; late subscribers still receive later events.
 *
 * Returns an unsubscribe function.
 */
export function addPushHandlers(handlers: PushNotificationHandlers): () => void {
  handlerSubscriptions.add(handlers);
  return () => {
    handlerSubscriptions.delete(handlers);
  };
}

/** Run one callback per subscriber, isolating a throwing subscriber from the others. */
function dispatch<K extends keyof PushNotificationHandlers>(
  event: K,
  invoke: (handler: NonNullable<PushNotificationHandlers[K]>) => void
): void {
  for (const subscription of handlerSubscriptions) {
    const handler = subscription[event];
    if (!handler) continue;
    try {
      invoke(handler as NonNullable<PushNotificationHandlers[K]>);
    } catch (error) {
      console.error(`[Push] Subscriber threw in ${String(event)}:`, error);
    }
  }
}

/**
 * Request push notification permissions.
 *
 * On Android 13+ (API 33) this is what surfaces the POST_NOTIFICATIONS runtime dialog.
 * Returns the resulting status rather than a bare boolean so callers can tell a denial
 * apart from an error — a denial is otherwise completely silent and the user just never
 * receives anything.
 */
export async function requestPushPermission(): Promise<PushPermissionStatus> {
  if (!isNative) {
    return requestWebPushPermission();
  }

  try {
    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') return 'granted';
    if (result.receive === 'denied') return 'denied';
    return 'prompt';
  } catch (error) {
    console.error('[Push] Error requesting permissions:', error);
    return 'denied';
  }
}

/**
 * Request web push permission (for browser)
 */
async function requestWebPushPermission(): Promise<PushPermissionStatus> {
  if (!('Notification' in window)) {
    console.warn('[Push] Notifications not supported in this browser');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  if (permission === 'granted') return 'granted';
  if (permission === 'denied') return 'denied';
  return 'prompt';
}

/**
 * Create the Android notification channel.
 *
 * Android 8+ silently drops notifications posted to a channel that does not exist, and
 * the channel must exist on the DEVICE — declaring the id in the manifest does not create
 * it. Creating an existing channel is a no-op, so this is safe on every launch.
 */
export async function ensureNotificationChannel(): Promise<void> {
  if (!isAndroid || channelEnsured) return;
  channelEnsured = true;

  try {
    await PushNotifications.createChannel({
      id: ANDROID_CHANNEL_ID,
      name: 'Trade Loop alerts',
      description: 'Referrals, messages and account activity',
      importance: 4, // IMPORTANCE_HIGH — heads-up banner
      visibility: 1, // VISIBILITY_PUBLIC — show content on the lock screen
      lights: true,
      lightColor: '#2563eb',
      vibration: true,
    });
    console.log('[Push] Notification channel ready:', ANDROID_CHANNEL_ID);
  } catch (error) {
    // Not fatal on its own — but every notification will be dropped, so say so loudly.
    console.error('[Push] Failed to create notification channel — notifications will be dropped:', error);
    channelEnsured = false;
  }
}

/**
 * Register for push notifications.
 *
 * Idempotent: installs listeners once and calls `PushNotifications.register()` once per
 * app session, so it is safe for both the startup initializer and the permission prompt
 * to call it. `handlers`, when supplied, is registered as a subscription for the caller's
 * convenience — it is NOT a second listener set — and is returned as an unsubscribe.
 */
export async function registerForPushNotifications(
  handlers?: PushNotificationHandlers
): Promise<() => void> {
  const unsubscribe = handlers ? addPushHandlers(handlers) : () => {};

  if (!isNative) {
    // Web push is handled by the existing service worker + VAPID flow.
    console.log('[Push] Web push handled by service worker');
    return unsubscribe;
  }

  try {
    // Listeners must exist BEFORE register(), or the `registration` event that carries
    // the token can fire with nothing listening and the token is lost for the session.
    installNativePushListeners();
    await ensureNotificationChannel();

    if (registerCalled) {
      console.log('[Push] Already registered this session, skipping duplicate register()');
      return unsubscribe;
    }

    try {
      registerCalled = true;
      await PushNotifications.register();
      console.log('[Push] Native push registration initiated');
    } catch (registerError: any) {
      // Expected when google-services.json / APNs config is missing — see
      // src/docs/ANDROID_PUSH_SETUP.md.
      registerCalled = false;
      console.warn(
        '[Push] Native registration failed (missing Firebase/APNs config?):',
        registerError?.message || registerError
      );
      // `register()` throwing synchronously is the other half of the signal — on Android a
      // missing google-services.json surfaces here rather than on the registrationError
      // listener, so both paths must record it (Card 17).
      // Stamped with the build, so a later, fixed build does not inherit this verdict.
      recordRegistrationError(registerError, await getNativeBuild());
      dispatch('onRegistrationError', (fn) => fn(registerError));
    }
  } catch (error: any) {
    console.error('[Push] Unexpected registration error:', error?.message || error);
    dispatch('onRegistrationError', (fn) => fn(error));
  }

  return unsubscribe;
}

/**
 * Install the one and only set of native push listeners.
 * Every subsequent call is a no-op — this is what makes registration idempotent.
 */
function installNativePushListeners(): void {
  if (listenersInstalled) return;
  listenersInstalled = true;

  try {
    // Registration success
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('[Push] Registration successful, token:', token.value.substring(0, 20) + '...');

      // A token proves the plugin AND the Firebase/APNs config are both present, so any
      // stored failure is now history — leaving it would make the diagnostics screen report
      // a build problem that has since been fixed (Card 17).
      clearRegistrationError();

      // Send the token to the backend first so a throwing subscriber cannot stop the
      // sync, then fan out. `sendTokenToServer` never rejects.
      await sendTokenToServer(token.value);
      dispatch('onRegistration', (fn) => fn(token.value));
    });
  } catch (e) {
    console.warn('[Push] Failed to add registration listener:', e);
  }

  try {
    // Registration error
    PushNotifications.addListener('registrationError', (error: any) => {
      console.warn('[Push] Registration error from native:', error);
      // Persisted for the diagnostics screen: this is the ONLY moment a missing
      // google-services.json / GoogleService-Info.plist announces itself, and it happens at
      // launch, long before anyone thinks to look (Card 17).
      void getNativeBuild().then((build) => recordRegistrationError(error, build));
      dispatch('onRegistrationError', (fn) => fn(error));
    });
  } catch (e) {
    console.warn('[Push] Failed to add registrationError listener:', e);
  }

  try {
    // Notification received while app is in the foreground
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('[Push] Notification received:', notification);
      // "FCM accepted the message" and "this device showed it" are different claims, and the
      // gap between them is where push bugs live. Record the arrival locally (Card 17).
      recordDeliverySeen({ title: notification?.title, body: notification?.body, via: 'foreground' });
      dispatch('onNotificationReceived', (fn) => fn(notification));
    });
  } catch (e) {
    console.warn('[Push] Failed to add pushNotificationReceived listener:', e);
  }

  try {
    // User tapped a notification (fires in background AND killed state)
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('[Push] Notification action:', action);
      // A tap is proof of delivery too — and on a killed-state launch it is the ONLY proof,
      // since `pushNotificationReceived` never fired for it (Card 17).
      recordDeliverySeen({
        title: action?.notification?.title,
        body: action?.notification?.body,
        via: 'tap'
      });
      dispatch('onNotificationAction', (fn) => fn(action));

      try {
        handleNotificationNavigation(action.notification.data);
      } catch (navError) {
        console.error('[Push] Error handling notification navigation:', navError);
      }
    });
  } catch (e) {
    console.warn('[Push] Failed to add pushNotificationActionPerformed listener:', e);
  }
}

/**
 * Send push token to backend server
 *
 * The relative `/api/...` URL is deliberate: on a bundled native build the origin shim in
 * `src/lib/nativeApiOrigin.ts` rewrites it onto the API origin and attaches the bearer
 * session, and on web it is already same-origin.
 *
 * ⚠️ THIS RUNS DURING STARTUP, BEFORE THE SESSION EXISTS. `initializeCapacitor()` is fired
 * from main.tsx at module scope, so `register()` — and therefore the `registration` event that
 * lands here — races React mounting. The native session id is only loaded out of the
 * Keychain/Keystore by `restoreSessionId()` inside an AuthContext effect, which cannot have run
 * yet if registration wins. Posting anyway sends the token with no `Authorization` header, and
 * `/api/push/register` answers 401.
 *
 * That is not hypothetical and it is not iOS-specific, though iOS is where it bit: on
 * 2026-08-16 an iPhone was found still registered under a token from 2026-08-01, because it lost
 * this race on every launch for sixteen days while Android happened to win it. The server kept
 * pushing to the dead token, FCM kept reporting success, and nothing arrived. Android is one
 * timing change away from the same thing.
 *
 * So: never post without a session, and make the pending flag mean something —
 * `syncPendingPushToken()` below is what finally sends it.
 */
async function sendTokenToServer(token: string): Promise<void> {
  // Store the token first, unconditionally — it is the input to every retry below.
  try {
    localStorage.setItem('push_token', token);
    localStorage.setItem('push_platform', platform);
  } catch {
    // Private-mode / quota failures are not worth failing registration over.
  }

  // Native authenticates by bearer from the secure store; no session id means the header
  // cannot be attached and the POST is a guaranteed 401. Defer instead of burning it.
  if (isNative && !getSessionId()) {
    localStorage.setItem('push_token_pending_sync', 'true');
    console.log('[Push] No session yet — deferring token sync until sign-in completes');
    return;
  }

  try {
    await axios.post(
      '/api/push/register',
      { token, platform },
      { withCredentials: true, timeout: 10000 }
    );
    localStorage.removeItem('push_token_pending_sync');
    console.log('[Push] Token sent to server successfully');
  } catch (error: any) {
    // Log but don't crash — the token is saved locally and `syncPendingPushToken` retries it.
    console.warn('[Push] Failed to send token to server (will retry):', error?.message || error);
    localStorage.setItem('push_token_pending_sync', 'true');
  }
}

/**
 * Flush a token the server never received.
 *
 * Called once the app knows it is authenticated (AuthContext), which is the earliest moment the
 * request can succeed. Before this existed, `push_token_pending_sync` was written by the failure
 * path above and read only by the diagnostics screen — the app told you the token was pending and
 * then never sent it, so a single unlucky launch disabled push permanently.
 *
 * Safe to call on every auth transition: it no-ops unless a token is stored AND the flag is set.
 */
export async function syncPendingPushToken(): Promise<void> {
  if (!isNative) return;
  if (localStorage.getItem('push_token_pending_sync') !== 'true') return;

  const token = localStorage.getItem('push_token');
  if (!token) {
    // Flag with no token is meaningless state — clear it rather than retrying forever.
    localStorage.removeItem('push_token_pending_sync');
    return;
  }

  if (!getSessionId()) {
    console.log('[Push] Pending token sync skipped — still no session');
    return;
  }

  console.log('[Push] Retrying deferred token sync');
  await sendTokenToServer(token);
}

/**
 * Handle navigation when a user taps a notification.
 *
 * Routes through the app's router when one has been registered (see
 * `setPushNavigationHandler`), and parks the destination otherwise. A killed-state tap
 * arrives before React mounts, which is exactly the case that used to be lost.
 */
function handleNotificationNavigation(data: any): void {
  const url: unknown = data?.url;
  if (typeof url !== 'string' || url.length === 0) return;

  if (!url.startsWith('/')) {
    // External URL — nothing for the router to do.
    window.open(url, '_blank');
    return;
  }

  if (navigationHandler) {
    navigationHandler(url);
    return;
  }

  console.log('[Push] Router not mounted yet, queuing tap-through destination:', url);
  pendingNavigation = url;
}

/**
 * Get the current push notification delivery status
 */
export async function getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
  if (!isNative) {
    return [];
  }

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch (error) {
    console.error('[Push] Error getting delivered notifications:', error);
    return [];
  }
}

/**
 * Remove all delivered notifications
 */
export async function clearAllNotifications(): Promise<void> {
  if (!isNative) {
    return;
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
    console.log('[Push] All notifications cleared');
  } catch (error) {
    console.error('[Push] Error clearing notifications:', error);
  }
}

/**
 * Check if push notifications are available and enabled
 */
export async function checkPushPermissionStatus(): Promise<PushPermissionStatus> {
  if (!isNative) {
    if (!('Notification' in window)) {
      return 'denied';
    }
    // Browser returns 'default' instead of 'prompt'
    const permission = Notification.permission;
    if (permission === 'default') return 'prompt';
    return permission as 'granted' | 'denied';
  }

  try {
    const result = await PushNotifications.checkPermissions();
    if (result.receive === 'granted') return 'granted';
    if (result.receive === 'denied') return 'denied';
    return 'prompt';
  } catch (error) {
    console.error('[Push] Error checking permissions:', error);
    return 'denied';
  }
}

/**
 * Initialize push notifications for the app. Called once from `initializeCapacitor()`.
 *
 * This is the path that re-syncs an ALREADY-GRANTED user's token on every launch — the
 * thing that was broken while the startup call installed no listeners. FCM rotates
 * tokens, so without it a returning user's stored token silently goes stale.
 */
export async function initializePushNotifications(handlers?: PushNotificationHandlers): Promise<void> {
  console.log('[Push] Initializing push notifications for platform:', platform);

  // The channel must exist before any notification arrives, including one that arrives
  // while permission is still undecided.
  await ensureNotificationChannel();

  const permissionStatus = await checkPushPermissionStatus();
  console.log('[Push] Current permission status:', permissionStatus);

  if (permissionStatus === 'granted') {
    await registerForPushNotifications(handlers);
  } else if (permissionStatus === 'prompt') {
    // Not yet requested — PushNotificationPrompt owns asking. Listeners still go in now
    // so nothing is missed between the grant and the register() call.
    if (isNative) installNativePushListeners();
    if (handlers) addPushHandlers(handlers);
    console.log('[Push] Permission not yet requested, waiting for user action');
  } else {
    console.log('[Push] Push notifications denied');
  }
}

/**
 * Test-only: drop all module state so each test starts from a clean session.
 * Not exported from the barrel and not referenced by app code.
 */
export function __resetPushStateForTests(): void {
  listenersInstalled = false;
  registerCalled = false;
  channelEnsured = false;
  handlerSubscriptions.clear();
  pendingNavigation = null;
  navigationHandler = null;
}
